import type { SyntheticEvent } from 'react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  centerCrop,
  convertToPixelCrop,
  type Crop,
  makeAspectCrop,
} from 'react-image-crop';

import { MAX_PHOTO_EDGE_PX } from '../../utils/downscale';
import type { NormalisedRect } from './webcam/faceFraming';
import { toPixelCrop } from './webcam/faceFraming';

function getCroppedBlob(image: HTMLImageElement, crop: Crop): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const size = Math.min(
    MAX_PHOTO_EDGE_PX,
    Math.round(Math.min(crop.width, crop.height) * Math.min(scaleX, scaleY)),
  );
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    size,
    size,
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas toBlob failed'));
        }
      },
      'image/jpeg',
      0.95,
    );
  });
}

interface CropFlowOptions {
  initialCrop: NormalisedRect | null;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void | Promise<void>;
  previewBeforeConfirm: boolean;
}

/**
 * The crop → (preview →) confirm sequence behind `CropModal`: the square being
 * edited, the busy/sending locks, the optional preview, the error line, and
 * the abandonment guard that keeps a dismissed crop from being delivered.
 */
export function useCropFlow({
  initialCrop,
  onCancel,
  onConfirm,
  previewBeforeConfirm,
}: CropFlowOptions) {
  const { t } = useTranslation();
  const [crop, setCrop] = useState<Crop>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Busy covers both halves of a confirm; this covers only the second. They are
  // not interchangeable: a slow `toBlob` must stay abandonable — the whole point
  // of `abandonedRef` — whereas an upload in flight must not be, because the
  // request lands whatever the operator clicks and cancelling would leave them
  // sure a photo was discarded that in fact arrived.
  const [sending, setSending] = useState(false);
  /** The cropped result awaiting approval. Null while still cropping. */
  const [preview, setPreview] = useState<{ blob: Blob; url: string } | null>(
    null,
  );
  const imgRef = useRef<HTMLImageElement>(null);
  // The state above drives the UI; this ref closes the window between two
  // clicks landing in the same frame, before React has re-rendered the
  // disabled button. Two POSTs for one person is two audit rows.
  const busyRef = useRef(false);
  // Cropping is async, and the modal can be dismissed while it runs — the
  // backdrop stays live, and an unmount needs no click at all. Without this the
  // continuation still fired `onConfirm`, uploading the photo the operator had
  // just cancelled. Invalidating the pending completion beats blocking every
  // dismissal path: a slow `toBlob` must not trap the operator in the modal.
  //
  // It guards the crop only. Once `onConfirm` is under way every dismissal path
  // is disabled (see `sending`), so there is no window left to abandon in.
  // Nothing ever sets it back: the invariant is that the consumer unmounts this
  // modal on `onCancel`, so each crop starts on a freshly mounted ref. Both
  // consumers honour it — the kiosk drops `pending`, PhotoBoard clears
  // `showCrop`. One that kept the modal mounted after cancelling would inherit
  // a permanently dead Confirm: `handleCrop` would crop and then return
  // without calling `onConfirm`, silently, with nothing on screen to say why.
  const abandonedRef = useRef(false);
  // Revokes the current preview URL; an effect event, so the unmount cleanup
  // (empty deps) reads the latest value, not the first render's.
  const revokePreview = useEffectEvent(() => {
    if (preview) {
      URL.revokeObjectURL(preview.url);
    }
  });

  function onImageLoad(e: SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    // Pixels, both ways. getCroppedBlob reads x/y/width/height as display
    // pixels and scales them to the source, so a `%` crop reaching it whole —
    // which is what happens when the operator confirms without dragging
    // anything, since `onChange` is what normalises the units — would have it
    // treat 90 as ninety pixels and cut a corner out of the photo.
    setCrop(
      initialCrop
        ? toPixelCrop(initialCrop, width, height)
        : convertToPixelCrop(
            centerCrop(
              makeAspectCrop({ unit: '%', width: 90 }, 1, width, height),
              width,
              height,
            ),
            width,
            height,
          ),
    );
  }

  /** Dismissal, from the Cancel button or the backdrop. */
  function dismiss() {
    abandonedRef.current = true;
    onCancel();
  }

  // Unmounting counts as abandoning too, and it takes no click: the kiosk drops
  // this modal when the operator selects somebody else mid-crop.
  useEffect(() => {
    return () => {
      abandonedRef.current = true;
      revokePreview();
    };
  }, []);

  /** Hand the button back so the operator can try the same crop again. */
  function failWith(message: string) {
    busyRef.current = false;
    setBusy(false);
    setSending(false);
    setError(message);
  }

  /**
   * Hand the blob to the consumer. The modal stays up for the whole call and
   * only a rejection returns control, so on success the consumer's unmount is
   * what ends this.
   */
  async function deliver(blob: Blob) {
    setSending(true);
    try {
      await onConfirm(blob);
      // No reset on success: the consumer closes the modal, and a second
      // confirm on a modal that is about to unmount is exactly what this
      // guard is for. Only a failure hands the button back.
    } catch {
      failWith(t('photoUpload.sendFailed'));
    }
  }

  async function handleCrop() {
    if (!imgRef.current || !crop || busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);

    // Two failures with nothing in common but the button they release. Cropping
    // failed means the canvas never produced an image; `onConfirm` failed means
    // the consumer could not do what it wanted with a perfectly good one — for
    // the kiosk, that the upload did not land. Telling the operator which is
    // the difference between re-cropping and simply pressing again.
    let blob: Blob;
    try {
      blob = await getCroppedBlob(imgRef.current, crop);
    } catch {
      failWith(t('photoUpload.cropFailed'));
      return;
    }
    if (abandonedRef.current) {
      return;
    }

    if (previewBeforeConfirm) {
      // Nothing is in flight while the operator looks, so the button comes
      // straight back — this crop is finished, the decision is theirs.
      busyRef.current = false;
      setBusy(false);
      setPreview({ blob, url: URL.createObjectURL(blob) });
      return;
    }
    await deliver(blob);
  }

  /** Approve the previewed square and hand it over. */
  function handleSend() {
    if (!preview || busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    void deliver(preview.blob);
  }

  /** Back to the editor. The rejected square is dropped, not kept around. */
  function backToCrop() {
    if (!preview) {
      return;
    }
    URL.revokeObjectURL(preview.url);
    setPreview(null);
    setError(null);
  }

  return {
    imgRef,
    crop,
    setCrop,
    error,
    busy,
    sending,
    preview,
    onImageLoad,
    dismiss,
    handleCrop,
    handleSend,
    backToCrop,
  };
}
